/**
 * PINNING THE IMPLICIT PRIMARY — the pure half.
 *
 * Three kinds of record say "the deck's first document" without naming it:
 *
 *   - a card ref with no `s:{sourceId}:` qualifier (`m:…`, `r:…`, and every
 *     legacy chunk index `3` / `2-4`) — `card-source-ref.ts`;
 *   - a mind-map node whose `source` carries no `sourceId` — `mindmap-types.ts`;
 *   - a `paragraphs` quiz attempt with no `quizSourceId` — `shared-types.ts`.
 *
 * Each of them means the document that was `sources[0]` WHEN IT WAS WRITTEN.
 * That is a stable reference only for as long as index 0 does not move, and
 * it moves in exactly three places: a batch document landing ahead of a
 * sibling that landed first, removing the first document, and undoing that
 * removal. Before any of those writes, the outgoing primary's implicit
 * records are pinned to its id — by these planners, applied by
 * `sync-client/deck-sources.ts` in the same write. Nothing else in the deck
 * changes: a pinned ref resolves to the very same document, only by name.
 * The same write stamps the newcomer ({@link stampFirstDocument}), so no
 * other copy's order can move index 0 back without a pin.
 *
 * Nothing here needs a migration. A record with no source id on a deck whose
 * first document never moved still means what it always did, and every
 * reader keeps resolving it against `sources[0]`.
 */

import type {
  DeckSource,
  DeckSourcePrimaryStamps,
  SyncPdfAnnotations,
  SyncQuizProgressData,
  SyncQuizProgressEntry,
} from '../../sync-engine/schema/shared-types';
import {
  isAnchoredCardSourceRef,
  parseCardSourceRef,
  qualifyCardSource,
} from './card-source-ref';
import { isPlaceholderSource, primarySourceId } from './manage';

/**
 * The id of the primary a `sources` write would DISPLACE, or `undefined`
 * when index 0 keeps naming the same document (or there was none). This is
 * the trigger for a pin: whoever writes `after` over `before` must first pin
 * the returned id's implicit records.
 *
 * A placeholder at index 0 (`manual`, or a hollow entry with no file and no
 * text) is not a document — nothing was ever anchored in it — so the first
 * real document taking its place displaces nothing. Pinning to the placeholder's id would anchor every bare record
 * of the deck to an id no document will ever answer to.
 */
export function displacedPrimary(
  before: readonly DeckSource[] | undefined,
  after: readonly DeckSource[] | undefined,
): string | undefined {
  const first = before?.[0];
  if (!first || isPlaceholderSource(first)) return undefined;
  const outgoing = first.sourceId;
  return primarySourceId(after) === outgoing ? undefined : outgoing;
}

/** One millisecond after `iso` — the smallest stamp that outranks it. */
function justAfterISO(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? iso : new Date(ms + 1).toISOString();
}

/**
 * The deck's first-document stamps after writing `after` over `before`, or
 * `undefined` when the write leaves the same document first (nothing to
 * record). Every write that makes a different REAL document first records it
 * here, in the same write — that stamp is what keeps it first when the row
 * meets a copy that still lists another document first
 * (`deck-materials-merge.ts`, `stampedFirst`). The stamp is strictly later
 * than every stamp the row already holds, so a device whose clock runs
 * behind still outranks the decision it saw.
 */
export function stampFirstDocument(
  stamps: DeckSourcePrimaryStamps | undefined,
  before: readonly DeckSource[] | undefined,
  after: readonly DeckSource[] | undefined,
  nowISO: string,
): DeckSourcePrimaryStamps | undefined {
  const next = after?.[0];
  if (!next || isPlaceholderSource(next)) return undefined;
  const previous = before?.[0];
  if (previous && !isPlaceholderSource(previous) && previous.sourceId === next.sourceId) {
    return undefined;
  }
  let at = nowISO;
  for (const seen of Object.values(stamps ?? {})) {
    if (seen >= at) at = justAfterISO(seen);
  }
  return { ...stamps, [next.sourceId]: at };
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export interface PinnedCardPatch {
  id: string;
  source: string;
}

/**
 * The card edits that pin every implicit ref to `sourceId`: bare `m:` / `r:`
 * refs and legacy chunk indices become `s:{sourceId}:…`. Already qualified
 * refs (to any source) and non-positional refs (manual, notes, opaque) are
 * left alone — the patch list holds only cards that actually change.
 */
export function pinCardSourcesToPrimary(
  cards: readonly { id: string; source?: string | null }[],
  sourceId: string,
): PinnedCardPatch[] {
  const patches: PinnedCardPatch[] = [];
  for (const card of cards) {
    const ref = parseCardSourceRef(card.source);
    if (!isAnchoredCardSourceRef(ref) || ref.sourceId !== undefined) continue;
    const pinned = qualifyCardSource(card.source, sourceId);
    if (pinned !== undefined && pinned !== card.source) {
      patches.push({ id: card.id, source: pinned });
    }
  }
  return patches;
}

// ---------------------------------------------------------------------------
// Mind maps
// ---------------------------------------------------------------------------

/** The part of a map node this module reads; everything else passes through. */
interface PinnableNode {
  source?: {
    sourceId?: string;
    kind?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * The map with every DOCUMENT-grounded node that names no document stamped
 * with `sourceId`. Notes- and flashcards-grounded nodes carry their excerpt
 * and never meant a document; nodes that already name one keep it.
 * `changed` is false when there was nothing to pin, so a caller can skip the
 * write.
 */
export function pinMindmapToPrimary<T extends { nodes: PinnableNode[] }>(
  mindmap: T,
  sourceId: string,
): { mindmap: T; changed: boolean } {
  let changed = false;
  const nodes = mindmap.nodes.map((node) => {
    if (!node.source) return node;
    const isDocument = node.source.kind === undefined || node.source.kind === 'document';
    if (!isDocument || node.source.sourceId !== undefined) return node;
    changed = true;
    return { ...node, source: { ...node.source, sourceId } };
  });
  return changed ? { mindmap: { ...mindmap, nodes }, changed } : { mindmap, changed };
}

/**
 * A map about to be SAVED, with every pin the stored copy carries kept.
 *
 * An open canvas seeds its nodes once and autosaves its own state, so a pin
 * written to the store while the map is on screen (the deck's documents moved
 * meanwhile) is not in the nodes the next edit writes back — and one drag
 * would have re-pointed every node at the new first document. A node the
 * canvas hands back unnamed takes the document the stored node of the same id
 * names; nodes that name one keep it, nodes with no source stay as they are.
 * The canvas never unpins a node — a citation's document is not something a
 * user edits — so carrying the pin forward can never undo an edit.
 */
export function keepPinnedNodeSources<T extends { nodes: PinnableNode[] }>(
  next: T,
  stored: { nodes: PinnableNode[] } | undefined,
): T {
  if (!stored) return next;
  const pinnedById = new Map<string, string>();
  for (const node of stored.nodes) {
    const id = node.id;
    const sourceId = node.source?.sourceId;
    if (typeof id === 'string' && typeof sourceId === 'string')
      pinnedById.set(id, sourceId);
  }
  if (pinnedById.size === 0) return next;
  let changed = false;
  const nodes = next.nodes.map((node) => {
    if (!node.source || node.source.sourceId !== undefined) return node;
    const isDocument = node.source.kind === undefined || node.source.kind === 'document';
    if (!isDocument) return node;
    const sourceId = typeof node.id === 'string' ? pinnedById.get(node.id) : undefined;
    if (sourceId === undefined) return node;
    changed = true;
    return { ...node, source: { ...node.source, sourceId } };
  });
  return changed ? { ...next, nodes } : next;
}

// ---------------------------------------------------------------------------
// Quiz progress
// ---------------------------------------------------------------------------

/**
 * The quiz entry with every `paragraphs` attempt that names no document
 * stamped with `sourceId`, or `null` when nothing in it needed pinning. A
 * flashcards quiz reads no document; an oral exam or a case study shares the
 * entity but not the attempt shape (`examKind`), and is left alone.
 */
export function pinQuizEntryToPrimary(
  entry: SyncQuizProgressEntry,
  sourceId: string,
): SyncQuizProgressEntry | null {
  if (entry.examKind) return null;
  let changed = false;
  const attempts = (entry.attempts ?? []).map((attempt: SyncQuizProgressData) => {
    if (attempt?.quizSourceType !== 'paragraphs' || attempt.quizSourceId) return attempt;
    changed = true;
    return { ...attempt, quizSourceId: sourceId };
  });
  return changed ? { ...entry, attempts } : null;
}

/**
 * A quiz entry about to be WRITTEN, with every document the stored copy's
 * attempts name kept.
 *
 * A running session rebuilds its attempt from its own state on every answer
 * and writes the whole entry back. An attempt that started without a
 * document (a main-era quiz resumed today) and was pinned to one in the store
 * meanwhile would come back unnamed on the next flush — and read the deck's
 * next document under the old one's name. An attempt the session writes
 * unnamed takes the document the stored attempt with the same `ts` names;
 * attempts that name one keep it. Only `paragraphs` attempts read a document.
 */
export function keepPinnedQuizDocuments(
  next: SyncQuizProgressEntry,
  stored: SyncQuizProgressEntry | undefined,
): SyncQuizProgressEntry {
  if (!stored?.attempts?.length || next.examKind) return next;
  const namedByTs = new Map<string, string>();
  for (const attempt of stored.attempts as SyncQuizProgressData[]) {
    if (attempt?.quizSourceType === 'paragraphs' && attempt.quizSourceId) {
      namedByTs.set(attempt.ts, attempt.quizSourceId);
    }
  }
  if (namedByTs.size === 0) return next;
  let changed = false;
  const attempts = (next.attempts ?? []).map((attempt: SyncQuizProgressData) => {
    if (attempt?.quizSourceType !== 'paragraphs' || attempt.quizSourceId) return attempt;
    const quizSourceId = namedByTs.get(attempt.ts);
    if (quizSourceId === undefined) return attempt;
    changed = true;
    return { ...attempt, quizSourceId };
  });
  return changed ? { ...next, attempts } : next;
}

// ---------------------------------------------------------------------------
// PDF annotations (notes, explanations, askKa)
// ---------------------------------------------------------------------------

/**
 * The deck's PDF annotations with every bare `m:` anchor stamped to
 * `sourceId`. A sticky note, an explanation or an Ask-Ka thread is anchored
 * at offsets in ONE document — the primary, implicitly, until now — and would
 * otherwise sit at the same offsets of whatever document comes first next.
 * `changed` is false when nothing needed pinning, so the caller can skip the
 * write.
 */
export function pinAnnotationsToPrimary<T extends SyncPdfAnnotations>(
  annotations: T,
  sourceId: string,
): { annotations: T; changed: boolean } {
  let changed = false;
  const pinList = <A extends { source: string }>(list: A[]): A[] =>
    list.map((annotation) => {
      const pinned = qualifyCardSource(annotation.source, sourceId);
      if (pinned === undefined || pinned === annotation.source) return annotation;
      changed = true;
      return { ...annotation, source: pinned };
    });
  const notes = pinList(annotations.notes ?? []);
  const explanations = pinList(annotations.explanations ?? []);
  const askKa = pinList(annotations.askKa ?? []);
  return changed
    ? { annotations: { ...annotations, notes, explanations, askKa }, changed }
    : { annotations, changed };
}
